import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Check, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Plans() {
  const { t } = useLang();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("monthly");
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data)); }, []);

  const subscribe = async (plan) => {
    const key = interval === "monthly" ? plan.lookup_key_monthly : plan.lookup_key_yearly;
    if (!key) { toast.error("Stripe price not configured for this plan"); return; }
    setSubscribing(plan.plan_id);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubscribing(null);
    }
  };

  return (
    <DashboardLayout
      title={t("plans.title")}
      subtitle={t("plans.subtitle")}
      right={
        <div className="inline-flex rounded-md border border-zinc-800 p-1 bg-zinc-900/40" data-testid="plans-interval-toggle">
          <button data-testid="interval-monthly" onClick={() => setInterval("monthly")} className={`px-3 h-7 text-xs rounded ${interval === "monthly" ? "bg-white text-zinc-950" : "text-zinc-400"}`}>{t("plans.monthly")}</button>
          <button data-testid="interval-yearly" onClick={() => setInterval("yearly")} className={`px-3 h-7 text-xs rounded ${interval === "yearly" ? "bg-white text-zinc-950" : "text-zinc-400"}`}>{t("plans.yearly")}</button>
        </div>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plans-grid">
        {plans.map((p, idx) => {
          const price = interval === "monthly" ? p.price_monthly : p.price_yearly;
          const featured = idx === 1;
          return (
            <div key={p.plan_id} className={`rounded-xl border p-6 flex flex-col ${featured ? "border-indigo-500/40 bg-indigo-500/5" : "border-zinc-800 bg-zinc-900/40"}`} data-testid={`plan-card-${p.name.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="overline">{p.name}</div>
                {featured && <span className="text-[10px] uppercase tracking-widest text-indigo-400">Popular</span>}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="stat-num text-zinc-100">${price?.toFixed(0) ?? "—"}</span>
                <span className="text-xs text-zinc-500">/ {interval === "monthly" ? "mo" : "yr"}</span>
              </div>
              <p className="text-sm text-zinc-400 mt-1">{p.description}</p>
              <ul className="mt-4 space-y-2 text-sm text-zinc-300 flex-1">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> {f}</li>
                ))}
              </ul>
              <button
                data-testid={`plan-subscribe-${p.name.toLowerCase()}`}
                disabled={subscribing === p.plan_id || user?.role === "super_admin"}
                onClick={() => subscribe(p)}
                className={`btn-scale mt-6 h-10 rounded-md font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60 ${featured ? "bg-white text-zinc-950 hover:bg-zinc-100" : "border border-zinc-700 text-zinc-100 hover:bg-zinc-900"}`}
              >
                <CreditCard className="w-4 h-4" /> {t("plans.subscribe")}
              </button>
              {user?.role === "super_admin" && <div className="mt-2 text-[11px] text-zinc-500 text-center">Super admin cannot subscribe</div>}
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
